<template>
	<div>
		<div v-if="damageCase">
			<div>
				<div class="text-connectProBlue font-bold mb-2">
					{{ $t('select_rental_car_class') }}
				</div>
				<div class="flex mb-4">
					<div
						class="border-2 rounded px-4 py-1 mr-1 cursor-pointer"
						:class="rentalCarClass === replacementRentalCarClass ? 'bg-connectProBlue text-white' : ''"
						v-for="rentalCarClass in rentalCarClasses"
						:key="rentalCarClass.id"
						@click="selectRentalCarClass(rentalCarClass)"
					>
						<div class="text-center" style="font-size: 18px">
							{{ rentalCarClass.class }}
						</div>
					</div>
				</div>
				<BaseCard
					padding="0"
					style-text="text-l"
					:title="$t('calculation_advance_repair_approval').toString()"
					v-if="!$store.getters.isProcessComprehensive"
				>
					<div class="flex mb-4">
						<div class="flex space-x-4 items-center">
							<DocumentFileUploader
								:allow-upload-in-app-container="true"
								ref="DocumentFileUploader.pre"
								:document="documents.pre"
								:instant-upload="false"
								@update:document="documents.pre = $event"
								@update:filename="
									preFileName = $event;
									preFileName_zip = null;
								"
							>
								<div>
									<BaseButton>
										<font-awesome-icon :icon="['fas', 'upload']" class="mr-2" />
										{{ $t('calculation_advance') }}
										{{ preDownload_zip ? $t('again') : '' }} {{ $t('pick') }}
									</BaseButton>
								</div>
							</DocumentFileUploader>
							<DocumentFileUploader
								:allow-upload-in-app-container="true"
								ref="DocumentFileUploader.pre_zip"
								:document="documents.pre_zip"
								:instant-upload="false"
								accept="application/zip, application/x-zip"
								@update:document="documents.pre_zip = $event"
								@update:filename="
									preFileName_zip = $event;
									preFileName = null;
								"
							>
								<div>
									<BaseButton>
										<font-awesome-icon :icon="['fas', 'upload']" class="mr-2" />
										DAT3-ZIP-File
										{{ preDownload_zip ? $t('again') : '' }} {{ $t('pick') }}
									</BaseButton>
								</div>
							</DocumentFileUploader>
							<div><span v-html="preFileName_zip || preFileName"></span></div>
						</div>
					</div>
					<div class="text-connectProBlue font-bold mb-2">
						{{ $t('classify_damage') }}
					</div>
					<div class="">
						<BaseSelect v-model="statusSelect" :options="liabilityStatusOptions" class="w-128 pr-2" @changed="changed.status = true" />
					</div>
					<div class="flex justify-end">
						<BaseButton
							:disabled="!changed.vorlaufige_zip && !changed.vorlaufige_pdf"
							:class="changed.vorlaufige_zip || changed.vorlaufige_pdf ? 'cursor-pointer btn-blue' : ''"
							@click="savePre"
							>{{ $t('upload') }}</BaseButton
						>
					</div>
				</BaseCard>
				<BaseCard padding="0" style-text="text-l" :title="$t('provide_expertise_separate_calculation').toString()">
					<div>
						<DocumentFileUploader
							:allow-upload-in-app-container="true"
							ref="DocumentFileUploader.expertise"
							:document="documents.expertise"
							:instant-upload="false"
							@update:document="documents.expertise = $event"
							@update:filename="expertiseFileName = $event"
						>
							<div>
								<BaseButton>
									<font-awesome-icon :icon="['fas', 'upload']" class="mr-2"></font-awesome-icon>
									{{ $t('expertise') }}
									{{ expertiseDownload ? $t('again') : '' }} {{ $t('pick') }}
								</BaseButton>
								<div><span v-html="expertiseFileName"></span></div>
							</div>
						</DocumentFileUploader>
						<div class="flex space-x-4 mt-4 items-center">
							<DocumentFileUploader
								:allow-upload-in-app-container="true"
								ref="DocumentFileUploader.final"
								:document="documents.final"
								:instant-upload="false"
								@update:document="documents.final = $event"
								@update:filename="
									finalFileName = $event;
									finalFileName_zip = null;
								"
							>
								<div>
									<BaseButton>
										<font-awesome-icon :icon="['fas', 'upload']" class="mr-2"></font-awesome-icon>
										{{ $t('expertise_calculation') }}
										{{ finalDownload ? $t('again') : '' }} {{ $t('pick') }}
									</BaseButton>
								</div>
							</DocumentFileUploader>
							<DocumentFileUploader
								:allow-upload-in-app-container="true"
								ref="DocumentFileUploader.final_zip"
								:document="documents.final_zip"
								:instant-upload="false"
								accept="application/zip, application/x-zip"
								@update:document="documents.final_zip = $event"
								@update:filename="
									finalFileName_zip = $event;
									finalFileName = null;
								"
							>
								<div>
									<BaseButton>
										<font-awesome-icon :icon="['fas', 'upload']" class="mr-2"></font-awesome-icon>
										{{ $t('calculation_dat3') }}
										{{ finalDownload_zip ? $t('again') : '' }} {{ $t('pick') }}
									</BaseButton>
								</div>
							</DocumentFileUploader>
							<div><span v-html="finalFileName_zip || finalFileName"></span></div>
						</div>
						<div v-if="isUserAllowedInLevel5or6 && $store.getters.isProcessComprehensive" class="mt-4 flex space-x-4">
							<BaseSelect v-model="statusSelect" :options="comprehensiveStatusOptions" class="w-96" @changed="changed.status = true" />
						</div>
					</div>

					<div class="flex justify-end">
						<BaseButton
							:disabled="!changed.gutachten || (!changed.finale_zip && !changed.finale_pdf)"
							:class="!changed.gutachten || (!changed.finale_zip && !changed.finale_pdf) ? '' : 'cursor-pointer btn-blue'"
							@click="evaluateDamageCaseType"
							>{{ $t('upload') }}</BaseButton
						>
					</div>
				</BaseCard>
			</div>
		</div>
		<div v-else>
			<LoadingSpinner />
		</div>
	</div>
</template>

<script>
import { DamageCaseStateEnum } from '@/api/generated/models';
import BaseCard from '@/components/damagecases/forms/BaseCard';
import DocumentFileUploader from '@/components/forms/inputs/DocumentFileUploader';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import { DamagecasesApiFactory, PdfApiFactory } from '@/api/generated';
import defaultConfiguration from '@/api/defaultConfiguration';
import apiDefaults from '@/api/defaultConfiguration';
import LoadingSpinner from '@/components/navigation/LoadingSpinner';
import { emptyCatch } from '@/plugins/Utils';
import Authentication from '@/mixins/Authentication';
import PDFRepository from '@/api/repositories/PDFRepository';

const pdfApiFactory = PdfApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios);

function updateDamageCase() {
	let replacementClass = this.replacementRentalCarClass.class;
	if (this.replacementRentalCarClass.id === 10) {
		replacementClass = 0;
	}
	return DamagecasesApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
		.damagecasesIdPut(this.$route.params.processId || this.$route.params.id, {
			status: this.statusSelect.value,
			replacementRentalCarClass: replacementClass,
		})
		.then(response => {
			this.changed.status = false;
			this.changed.vorlaufige_pdf = false;
			this.changed.vorlaufige_zip = false;
			this.$store.dispatch('forceUpdateDamageCase');
		});
}

export default {
	name: 'RepairApproval',
	components: {
		LoadingSpinner,
		BaseSelect,
		BaseButton,
		DocumentFileUploader,
		BaseCard,
	},
	mixins: [Authentication],
	created() {
		if (!this.isUserAllowedInLevel2) {
			this.$emit('authNotAllowed');
		} else {
			this.$store.dispatch('updateDamageCase', { id: this.$route.params.processId || this.$route.params.id }).then(response => {
				this.$store.dispatch('updateDocuments', { id: this.$route.params.processId || this.$route.params.id });
				this.statusSelect = this.liabilityStatusOptions.find(option => option.value === this.$store.getters.getDamageCase.status);
				if (this.$store.getters.getDamageCase.replacementRentalCarClass === 0) {
					this.replacementRentalCarClass = this.rentalCarClasses.find(rentalCarClass => rentalCarClass.id === 10);
				} else {
					this.replacementRentalCarClass = this.rentalCarClasses.find(
						rentalCarClass => rentalCarClass.class === this.$store.getters.getDamageCase.replacementRentalCarClass,
					);
				}
			});

			new PDFRepository()
				.getPDFFile(this.$route.params.processId || this.$route.params.id, 'vorläufige-kalkulation')
				.then(response => {
					this.documents.pre = response.data.data;
				})
				.catch(emptyCatch);

			new PDFRepository()
				.getPDFFile(this.$route.params.processId || this.$route.params.id, 'vorläufige-kalkulation (dat3 zip‐file)')
				.then(response => {
					this.documents.pre_zip = response.data.data;
				})
				.catch(emptyCatch);

			new PDFRepository()
				.getPDFFile(this.$route.params.processId || this.$route.params.id, 'finale-kalkulation')
				.then(response => {
					this.documents.final = response.data.data;
				})
				.catch(emptyCatch);

			new PDFRepository()
				.getPDFFile(this.$route.params.processId || this.$route.params.id, 'gutachten')
				.then(response => {
					this.documents.expertise = response.data.data;
				})
				.catch(emptyCatch);
		}
	},
	data() {
		return {
			isProcessFinished: false,
			statusSelect: null,
			preFileName: null,
			preFileName_zip: null,
			finalFileName: null,
			finalFileName_zip: null,
			expertiseFileName: null,
			rentalCarClasses: [
				{
					class: 1,
					id: 0,
				},
				{
					class: 2,
					id: 1,
				},
				{
					class: 3,
					id: 2,
				},
				{
					class: 4,
					id: 3,
				},
				{
					class: 5,
					id: 4,
				},
				{
					class: 6,
					id: 5,
				},
				{
					class: 7,
					id: 6,
				},
				{
					class: 8,
					id: 7,
				},
				{
					class: 9,
					id: 8,
				},
				{
					class: 10,
					id: 9,
				},
				{
					class: 'nicht ermittelbar',
					id: 10,
				},
			],
			replacementRentalCarClass: null,
			documents: {
				expertise: {
					type: 'gutachten',
				},
				pre: {
					type: 'vorläufige-kalkulation',
				},
				pre_zip: {
					type: 'vorläufige-kalkulation (dat3 zip)',
				},
				final: {
					type: 'finale-kalkulation',
				},
				final_zip: {
					type: 'finale-kalkulation (dat3 zip)',
				},
			},
			liabilityStatusOptions: [
				{
					value: DamageCaseStateEnum.Repair100,
					display: 'Schaden bis 100% WBW – techn. Reparaturfreigabe durch SV',
				},
				{
					value: DamageCaseStateEnum.Repair130,
					display: 'Schaden bis 130% WBW – techn. Reparaturfreigabe durch SV',
				},
				{
					value: DamageCaseStateEnum.WriteOff,
					display: 'Totalschaden, keine techn. Reparaturfreigabe durch SV',
				},
			],
			comprehensiveStatusOptions: [
				{
					value: DamageCaseStateEnum.Repair100,
					display: 'Schaden bis 100% WBW – techn. Reparaturfreigabe durch SV',
				},
				{
					value: DamageCaseStateEnum.WriteOff,
					display: 'Totalschaden, keine techn. Reparaturfreigabe durch SV',
				},
			],
			changed: {
				vorlaufige_pdf: false,
				vorlaufige_zip: false,
				status: false,
				finale_zip: false,
				finale_pdf: false,
				gutachten: false,
			},
		};
	},
	computed: {
		damageCase() {
			return this.$store.getters.getDamageCase;
		},
		getAuthenticatedUserGroupLevel() {
			return this.$store.getters.getAuthenticatedUserGroupLevel;
		},
		preDownload() {
			return this.generateDownloadURL(this.$store.getters.getDocumentByType('vorläufige-kalkulation'));
		},
		preDownload_zip() {
			return this.generateDownloadURL(this.$store.getters.getDocumentByType('vorläufige-kalkulation (dat3 zip)'));
		},
		finalDownload() {
			return this.generateDownloadURL(this.$store.getters.getDocumentByType('finale-kalkulation'));
		},
		finalDownload_zip() {
			return this.generateDownloadURL(this.$store.getters.getDocumentByType('finale-kalkulation (dat3 zip)'));
		},
		expertiseDownload() {
			return this.generateDownloadURL(this.$store.getters.getDocumentByType('gutachten'));
		},
	},
	methods: {
		selectRentalCarClass(rentalCarClass) {
			if (this.replacementRentalCarClass === rentalCarClass) {
				this.replacementRentalCarClass = null;
			} else {
				this.replacementRentalCarClass = rentalCarClass;
			}
		},
		setLeaveMessage() {
			if ((this.changed.status || this.changed.vorlaufige_pdf || this.changed.vorlaufige_zip) && !this.$store.getters.isProcessComprehensive) {
				this.$store.commit('setConfirmLeaving', this.$t('changes_file_not_saved'));
			} else if (this.changed.gutachten || this.changed.finale_pdf || this.changed.finale_zip) {
				this.$store.commit('setConfirmLeaving', this.$t('changes_file_not_saved'));
			} else {
				this.$store.commit('setConfirmLeaving', false);
			}
		},
		savePre: function() {
			if (this.replacementRentalCarClass != null) {
				if ((this.preFileName_zip || this.preFileName) && this.statusSelect) {
					updateDamageCase.call(this).then(() => {
						const ref = this.preFileName ? this.$refs['DocumentFileUploader.pre'] : this.$refs['DocumentFileUploader.pre_zip'];
						ref
							.triggerFileUpload()
							.then(response => {
								this.$swal(this.$t('general.success').toString(), this.$t('files_uploaded').toString(), 'success').then(() => {
									this.$router.push({
										name: 'management.process.edit',
										params: { processId: this.$store.getters.getDamageCase.id, customer: this.$store.getters.getDamageCase.customer.id },
									});
								});
							})
							.catch(error => this.$swal(this.$t('general.error').toString(), this.$t('calculation_upload_error').toString(), 'error'));
					});
				} else if (this.preDownload && this.statusSelect) {
					updateDamageCase.call(this);
				} else if ((this.preFileName_zip || this.preFileName) && !this.statusSelect) {
					this.$swal(this.$t('general.attention').toString(), this.$t('repair_status_missing').toString(), 'warning');
				} else {
					this.$swal(this.$t('general.attention').toString(), this.$t('pick_calculation_and_status').toString(), 'warning');
				}
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_select_rental_car_class').toString(), 'warning');
			}
		},
		evaluateDamageCaseType() {
			if (this.$store.getters.isProcessComprehensive && this.statusSelect) {
				updateDamageCase.call(this).then(() => {
					this.saveFinal();
				});
			} else {
				this.saveFinal();
			}
		},
		saveFinal() {
			if (this.replacementRentalCarClass != null) {
				if ((this.expertiseFileName || this.expertiseDownload) && (this.finalFileName_zip || this.finalDownload_zip || this.finalFileName)) {
					updateDamageCase.call(this).then(() => {
						const promise = [];

						if (this.finalFileName) {
							promise.push(this.$refs['DocumentFileUploader.final'].triggerFileUpload());
						} else if (this.finalFileName_zip) {
							promise.push(this.$refs['DocumentFileUploader.final_zip'].triggerFileUpload());
						}

						if (this.expertiseFileName) {
							promise.push(this.$refs['DocumentFileUploader.expertise'].triggerFileUpload());
						}

						Promise.all(promise)
							.then(response => {
								this.changed.gutachten = false;
								this.changed.finale_zip = false;
								this.changed.finale_pdf = false;
								this.$swal(this.$t('general.success').toString(), this.$t('files_uploaded').toString(), 'success').then(() => {
									this.$router.push({
										name: 'management.process.edit',
										params: { processId: this.$store.getters.getDamageCase.id, customer: this.$store.getters.getDamageCase.customer.id },
									});
								});
							})
							.catch(error => {
								this.$swal(this.$t('general.error').toString(), this.$t('files_upload_error').toString(), 'error');
							});
					});
				} else {
					this.$swal(this.$t('general.attention').toString(), this.$t('choose_calculation_and_expertise').toString(), 'warning');
				}
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_select_rental_car_class').toString(), 'warning');
			}
		},
		generateDownloadURL(document) {
			return document?.uuid ? apiDefaults.axios.defaults.baseURL + 'api/pdf/damagecases/' + document.uuid + '/file' : null;
		},
	},
	watch: {
		changed: {
			deep: true,
			handler() {
				this.setLeaveMessage();
			},
		},

		finalFileName: function() {
			this.changed.finale_pdf = true;
		},
		finalFileName_zip: function() {
			this.changed.finale_zip = true;
		},
		preFileName: function() {
			this.changed.vorlaufige_pdf = true;
		},
		preFileName_zip: function() {
			this.changed.vorlaufige_zip = true;
		},
		expertiseFileName: function() {
			this.changed.gutachten = true;
		},
	},
};
</script>
